import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {PersonMultipleLine} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import {Title} from 'components/DSYS/Title';
import ProfileCard from 'components/shared/ProfileCard';
import {t} from 'i18next';
import type {ComponentType, ReactNode, SVGProps} from 'react';
import {getProfileImageUrl} from 'utilities';

import styles from './TeamBody.module.css';

export interface TeamMemberSummary {
  user_id: string;
  ldap: string;
  name: string;
  role?: string | null;
  additionalProfileInfo?: string;
  viewOnly?: boolean;
}

export const TeamMembersCard = ({
  members,
  title = t('members_title'),
  emptyText = t('no_members'),
  onEdit,
  beforeMembers,
  icon = PersonMultipleLine,
  source = 'teams',
  onClick,
  useSingleColumn = false,
}: {
  members: TeamMemberSummary[];
  title?: ReactNode;
  emptyText?: ReactNode;
  onEdit?: () => void;
  beforeMembers?: ReactNode;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  source?: string;
  onClick?: () => void;
  useSingleColumn?: boolean;
}) => (
  <Stack
    gap="8"
    className={atoms({
      paddingX: '20',
      paddingY: '16',
      borderColor: 'Border Subtle',
      borderWidth: '1',
      borderStyle: 'Solid',
      borderRadius: 'Medium',
    })}
  >
    <Title
      size="small"
      withAccessoryStart={<UIIcon src={icon} />}
      withAccessoryEnd={
        onEdit ? (
          <Button size="small" variant="transparent" onClick={onEdit}>
            {t('edit')}
          </Button>
        ) : undefined
      }
    >
      {title}
    </Title>
    {members.length > 0 || beforeMembers ? (
      <Box marginTop="24">
        {beforeMembers}
        <div
          className={cx(styles.teamMembersContainer, {
            [styles.teamMembersContainerSingleColumn]: useSingleColumn,
          })}
        >
          {members.map((person, index) => (
            <ProfileCard
              key={person.user_id || `${person.ldap}-${index}`}
              source={source}
              profile_username={person.ldap}
              profile_photo_url={person.ldap ? getProfileImageUrl(person.ldap) : undefined}
              display_name={person.name}
              profile_info={person.role}
              additional_profile_info={person.additionalProfileInfo}
              viewOnly={person.viewOnly}
              onClick={onClick}
            />
          ))}
        </div>
      </Box>
    ) : (
      <Text variant="paragraph" size="large" tagName="p" color="faint">
        {emptyText}
      </Text>
    )}
  </Stack>
);
